<template>
  <main>
    <background-wrapper :src="routeNameKebab + '/hero'" ext="png" :aria-label="$t(`img.${$route.name}.hero`)" content-padding="0 0 100px">
      <b-row class="hero home-hero" :class="[frenchClass]">
        <b-col cols="12" sm="8" md="7" lg="6">
          <p class="main" v-html="$t(`${$route.name}.hero.title`)"/>
          <p class="secondary" v-html="$t(`${$route.name}.hero.subtitle`)"/>
          <b-btn variant="primary" v-scroll-to="{ el: '#screenshot_images', offset: -20 }">{{$t(`${$route.name}.hero.btn`)}}</b-btn>
        </b-col>
        <b-col>
          <img class="hero-img d-none d-sm-block" :src="require('@/assets/home/hero-lady.png')" aria-hidden="true" alt="" role="presentation">
          <img class="hero-img d-block d-sm-none" :src="require('@/assets/home/hero-sm.png')" aria-hidden="true" alt="" role="presentation">
        </b-col>
      </b-row>
    </background-wrapper>
    <b-row class="capterra-google">
      <b-col class="text-center">
        <img :src="require('@/assets/home/Reviews.png')" alt="" role="presentation">
      </b-col>
    </b-row>
    <div class="screenshots-wrapper" id="screenshot_images">
      <text-container size="xl" justify-center align-center>
        <p class="section-title" v-html="$t(`${$route.name}.screenshots.title`)" />
      </text-container>
      <div class="screenshots">
        <video class="d-none d-lg-block" preload="auto" playsinline autoplay muted>
          <source :src="require('@/assets/home/videos/screenshots.mp4')" />
        </video>
        <img class="d-block d-lg-none" :src="require('@/assets/home/screenshots.png')" alt="" role="presentation" />
      </div>
    </div>
    <div class="products-wrapper" :class="[frenchClass]">
      <text-container size="lg" justify-center align-center>
        <p class="section-title" v-html="$t(`${$route.name}.products.title`)" />
        <p class="section-subtitle mt-2 mb-4" v-html="$t(`${$route.name}.products.subtitle`)" />
      </text-container>
      <b-row class="product-options-row m-auto d-none d-sm-flex">
        <div class="product-options">
          <button
            v-for="product in filteredProducts"
            :key="`product-button-${product}`"
            :class="[product]"
            class="btn product"
            @click="setActiveProduct(product)"
          >
            <img :src="require(`@/assets/home/products/icons/${product}-sm.png`)" aria-hidden="true" alt="" role="presentation">
            <span v-html="$t(`${$route.name}.products.options.${product}`)" />
          </button>
        </div>
      </b-row>
      <b-row class="d-none d-sm-flex">
        <b-col>
          <div class="product-details-container">
            <div class="product-details">
              <div class="name-container">
                <transition name="fade">
                  <img :src="require(`@/assets/home/products/icons/${activeProduct}-sm.png`)" aria-hidden="true" :key="activeProduct" :alt="`${activeProduct}-icon`" role="presentation">
                </transition>
                <div
                  v-html="$t(`${$route.name}.products.${activeProduct}.title`)"
                  :class="{ tall: isTallName }"
                  class="name"
                />
                <b-button variant="link" :to="productCta">{{ $t(`${$route.name}.products.learnMore`) }} <img aria-hidden="true" :src="require('@/assets/home/chevron-right.png')" alt="" role="presentation"></b-button>
              </div>
              <p class="description d-block d-lg-none" v-html=" $t(`${$route.name}.products.${activeProduct}.description`)" />
              <b-button class="d-block d-lg-none" variant="link">{{ $t(`${$route.name}.products.learnMore`) }}</b-button>
              <div class="word-carousel d-none d-lg-flex">
                <div
                  v-html="$t(`${$route.name}.products.${activeProduct}.spinnerTitle`)"
                  class="title"
                />

                <div class='word-carousel-wrapper'>
                  <div class='word-carousel'>
                    <!-- Double the words for an infinite loop -->
                    <div
                      v-for="(word, index) in $t(`${$route.name}.products.${activeProduct}.words`)"
                      :key="`${activeProduct}-word-${index}`"
                      class='word-carousel__item'
                    >
                      <div>{{ word }}</div>
                    </div>
                    <div
                      v-for="(word, index) in $t(`${$route.name}.products.${activeProduct}.words`)"
                      :key="`${activeProduct}-word--${index}`"
                      class='word-carousel__item'
                    >
                      <div>{{ word }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="computer">
              <div class="screens">
                <b-carousel
                  id="product_screens_carousel"
                  v-model="slide"
                  :interval="0"
                >
                  <b-carousel-slide
                    v-for="product in products"
                    :key="`product-slide-${product}`"
                    :img-src="require(`@/assets/home/products/screenshots/${product}.png`)"
                  />
                </b-carousel>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <div class="hr-market-mobile d-block d-sm-none">
        <HomeTools/>
      </div>
    </div>

    <b-row class="awards-container justify-content-center">
      <b-col class="text-center">
        <img :src="require('@/assets/home/Awards.png')" alt="" role="presentation">
      </b-col>
    </b-row>

    <ContentWithImage src="home/Onboarding" image-order="0" image-order-sm="0" margin="0 0 200px">
      <text-container size="sm">
        <p class="section-title large" v-html="$t(`${$route.name}.onboarding.title`)" />
        <p class="section-description" v-html="$t(`${$route.name}.onboarding.description`)" />
        <b-btn class="section-cta" variant="primary" to="/request-a-demo">{{ $t(`${$route.name}.onboarding.cta`) }}</b-btn>
      </text-container>
    </ContentWithImage>

    <ContentWithImage src="home/Workflows" image-order="1" image-order-sm="0" margin="0 0 200px">
      <text-container size="sm" justify-end>
        <p class="section-title large" v-html="$t(`${$route.name}.workflows.title`)" />
        <p class="section-description" v-html="$t(`${$route.name}.workflows.description`)" />
        <b-btn class="section-cta" variant="primary" to="/product/drive">{{ $t(`${$route.name}.workflows.cta`) }}</b-btn>
      </text-container>
    </ContentWithImage>

    <ContentWithImage src="home/Documentation" image-order="0" image-order-sm="0" margin="0 0 200px">
      <text-container size="sm">
        <p class="section-title large" v-html="$t(`${$route.name}.documentation.title`)" />
        <p class="section-description" v-html="$t(`${$route.name}.documentation.description`)" />
        <b-btn class="section-cta" variant="primary" to="/product/fundamentals">{{ $t(`${$route.name}.documentation.cta`) }}</b-btn>
      </text-container>
    </ContentWithImage>

    <ContentWithImage src="home/Training" image-order="1" image-order-sm="0" margin="0 0 200px">
      <text-container size="sm" justify-end>
        <p class="section-title large" v-html="$t(`${$route.name}.training.title`)" />
        <p class="section-description" v-html="$t(`${$route.name}.training.description`)" />
        <b-btn class="section-cta" variant="primary" to="/product/training">{{ $t(`${$route.name}.training.cta`) }}</b-btn>
      </text-container>
    </ContentWithImage>

    <ContentWithImage src="home/HR Compliance" image-order="0" image-order-sm="0" margin="0 0 200px">
      <text-container size="sm">
        <p class="section-title large" v-html="$t(`${$route.name}.compliance.title`)" />
        <p class="section-description" v-html="$t(`${$route.name}.compliance.description`)" />
        <b-btn class="section-cta" variant="primary" to="/request-a-demo">{{ $t(`${$route.name}.compliance.cta`) }}</b-btn>
      </text-container>
    </ContentWithImage>

    <ContentWithImage src="home/Who We Are" align-items="start" img-width="500px" image-order="1" image-order-sm="0" margin="0 0 200px">
      <text-container size="sm" justify-end>
          <p class="section-title large" v-html="$t(`${$route.name}.canadians.title`)" />
          <p class="section-description pr-lg-2 pr-0" v-html="$t(`${$route.name}.canadians.description`)" />
          <div class="section-awards">
            <img :src="require('@/assets/home/best-workplaces.png')" alt="" role="presentation">
            <img :src="require('@/assets/home/GPTW-2019.png')" alt="" role="presentation">
            <img :src="require('@/assets/home/GPTW-2022.png')" alt="" role="presentation">
          </div>
          <b-btn class="section-cta" variant="primary" to="/about-us">{{ $t(`${$route.name}.canadians.cta`) }}</b-btn>
      </text-container>
    </ContentWithImage>

    <b-row>
      <b-col class="trusted-by" :class="[frenchClass]">
        <div class="trusted-title" v-html="$t(`${$route.name}.trusted`)" />
        <div class="slider-wrapper">
          <VueSlickCarousel :arrows="true" v-bind="settings">
            <div v-for="n in 13" :key="n">
              <div class="great-companies__image">
                <img :src="require(`@/assets/${$toKebabCase($route.name)}/slider/slide_${n}.png`)" alt="" role="presentation">
              </div>
            </div>
            <template #prevArrow>
              <div class="custom-arrow">
                <img src="@/assets/home/chevron-left.svg" alt="" role="presentation">
              </div>
            </template>
            <template #nextArrow>
              <div class="custom-arrow">
                <img src="@/assets/home/chevron-right.svg" alt="" role="presentation">
              </div>
            </template>
          </VueSlickCarousel>
        </div>
      </b-col>
    </b-row>

    <b-row class="testimonials">
      <b-col class="testimonial" sm="12" md="6" lg="6">
        <img src="@/assets/home/Testimonial1.png" alt="" role="presentation">
        <div class="quote" v-html="$t(`${$route.name}.testimonials.1.quote`)" />
        <div class="cite blue" v-html="$t(`${$route.name}.testimonials.1.cite`)" />
      </b-col>
      <b-col class="testimonial" sm="12" md="6" lg="6">
        <img src="@/assets/home/Testimonial2.png" alt="" role="presentation">
        <div class="quote" v-html="$t(`${$route.name}.testimonials.2.quote`)" />
        <div class="cite blue" v-html="$t(`${$route.name}.testimonials.2.cite`)" />
      </b-col>
    </b-row>

    <background-wrapper :src="routeNameKebab + '/footer'" ext="png" :aria-label="$t(`img.${$route.name}.hero`)" align="top" content-padding="100px 0 0">
      <b-row class="footer">
        <b-col cols="8" sm="8" md="8" lg="5">
          <p class="title" v-html="$t(`${$route.name}.footer.title`)" />
          <p v-html="$t(`${$route.name}.footer.description`)" />
          <b-btn variant="primary" to="/request-a-demo" v-html="$t(`${$route.name}.footer.cta`)" />
        </b-col>
        <b-col>
          <video class="d-none d-lg-block" preload="auto" playsinline autoplay muted loop>
            <source :src="require('@/assets/home/videos/phone-tablet.webm')" />
          </video>
          <video class="d-block d-lg-none" preload="auto" playsinline autoplay muted loop>
            <source :src="require('@/assets/home/videos/phone-only.webm')" />
          </video>
        </b-col>
      </b-row>
    </background-wrapper>

    <script type="application/ld+json">
      {
        "@context": "https://schema.org",
        "@type": "Corporation",
        "name": "HRdownloads",
        "legalName" : "HR Downloads Inc.",
        "url": "https://www.hrdownloads.com",
        "logo": " https://pbs.twimg.com/profile_images/597852345402294272/uZZhUJMD_400x400.png",
        "foundingDate": "April 2008",
        "naics": "561410",
        "founders": [
          {
            "@type": "Person",
            "name": "Anthony Boyle"
          }
        ],
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "London, Ontario",
          "addressRegion": "ON",
          "addressCountry": "CA",
          "postalCode": "N6E 1L3",
          "streetAddress": "727 Exeter Rd"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "[+1 877-438-9763]",
          "contactType": "Customer Support"
        },
        "sameAs": [
          "https://www.facebook.com/HRdownloads",
          "https://twitter.com/HRdownloadsinc",
          "https://www.instagram.com/hrdownloads/?ref=badge",
          "https://www.linkedin.com/company/hrdownloads"
        ]
      }
    </script>
  </main>
</template>

<script>
import VueSlickCarousel from 'vue-slick-carousel'
import 'vue-slick-carousel/dist/vue-slick-carousel.css'
import 'vue-slick-carousel/dist/vue-slick-carousel-theme.css'
import { HomeTools, HomeToolsSlider } from '@/components/ui'
import ContentWithImage from '@/components/ui/ContentWithImage.vue'

export default {
  name: 'Home',
  data () {
    return {
      activeProduct: 'fundamentals',
      slide: 0,
      settings: {
        slidesToShow: 6,
        slidesToScroll: 1,
        autoplay: true,
        speed: 1000,
        autoplaySpeed: 2000,
        responsive: [
          {
            breakpoint: 769,
            settings: {
              slidesToShow: 4
            }
          },
          {
            breakpoint: 600,
            settings: {
              slidesToShow: 3
            }
          }
        ]
      },
      products: [
        'fundamentals',
        'drive',
        'training',
        'surveys',
        'job-description-generator',
        'policy-manual-wizard',
        'live-hr-advice',
        'hello-hr'
      ]
    }
  },
  computed: {
    routeNameKebab: app => app.$toKebabCase(app.$route.name),

    isFrench: app => app.$i18n.locale === 'fr',

    frenchClass: app => app.isFrench ? 'french' : '',

    isTallName () {
      const products = ['job-description-generator']
      const frenchProducts = ['policy-manual-wizard', 'live-hr-advice']
      const arr = this.isFrench ? [...products, ...frenchProducts] : products

      return arr.includes(this.activeProduct)
    },

    filteredProducts () {
      const excludeFrench = ['job-description-generator', 'hello-hr']

      return this.isFrench
        ? this.products.filter(product => !excludeFrench.includes(product))
        : this.products
    },

    productCta () {
      const url = this.activeProduct === 'hello-hr' ? 'hellohr' : `product/${this.activeProduct}`
      return `/${url}`
    }
  },
  components: { VueSlickCarousel, HomeTools, HomeToolsSlider, ContentWithImage },
  methods: {
    setActiveProduct (product) {
      const products = document.querySelectorAll('.product')
      const activeProduct = document.querySelector(`.product.${product}`)
      products.forEach(product => product.classList.remove('active'))
      activeProduct.classList.add('active')

      const ssIndex = this.products.findIndex(prod => prod === product)
      this.slide = ssIndex

      this.activeProduct = product
    }
  },
  mounted () {
    const firstProduct = document.querySelector('.product')

    if (firstProduct) firstProduct.classList.add('active')
  }
}
</script>

<style lang="sass" scoped>
$animation-timing: 20s
$carousel-items: 10
$animation-delay-fraction: $animation-timing / $carousel-items
$animation-steps-fraction: 100 / $carousel-items
$slide-change-timing: 3 // percentage of total animation cycle

// HERO
.hero
  padding: 100px 0 250px
  color: white
  position: relative
  +media-breakpoint-down(lg)
    padding: 100px 0 200px
  +media-breakpoint-down(md)
    padding: 50px 0 100px
  +media-breakpoint-down(xs)
    padding: 40px 0 50px
  p.main
    font-size: 66px
    line-height: 70px
    margin: 0 0 10px
    +media-breakpoint-down(lg)
      font-size: 60px
      line-height: 66px
    +media-breakpoint-down(md)
      font-size: 50px
      line-height: 53px
    +media-breakpoint-down(xs)
      font-size: 40px
      line-height: 41px
  p.secondary
    font-size: 25px
    line-height: 30px
    margin: 0 0 10px
    max-width: 300px
    +media-breakpoint-down(lg)
      font-size: 20px
      max-width: 250px
    +media-breakpoint-down(md)
      max-width: 200px
    +media-breakpoint-down(xs)
      max-width: 350px
  button
    background: white
    border-color: #006FD6
    color: #006FD6
    font-size: 20px
    margin-top: 42px
    +media-breakpoint-down(md)
      font-size: 16px
      margin-top: 30px
    +media-breakpoint-down(xs)
      font-size: 14px
      margin-top: 20px
  &-img
    width: 900px
    max-width: unset
    position: absolute
    left: -280px
    top: -50px
    +media-breakpoint-down(lg)
      width: 760px
      left: -230px
      top: -30px
    +media-breakpoint-down(md)
      width: 505px
      top: 20px
      left: -200px
    +media-breakpoint-down(sm)
      left: -250px
    +media-breakpoint-down(xs)
      width: 400px
      top: -130px
      left: 200px
    +media-breakpoint-down(xxs)
      left: 150px
  &.french
    .hero-img
      +media-breakpoint-down(lg)
        width: 700px
        left: -150px
      +media-breakpoint-down(md)
        width: 505px
        left: -150px
      +media-breakpoint-down(sm)
        left: -150px
      +media-breakpoint-down(xs)
        width: 400px
        top: -150px
        left: 200px
      +media-breakpoint-down(xxs)
        left: 200px

// CAPTERRA & GOOGLE RATINGS
.capterra-google
  img
    +media-breakpoint-down(md)
      width: 280px
    +media-breakpoint-down(xs)
      width: 210px

// SCREENSHOTS VIDEO
.screenshots-wrapper
  padding: 100px 0 0
  +media-breakpoint-down(md)
    padding: 60px 0 0
  .section-title:not(.large)
    +media-breakpoint-down(lg)
      max-width: 660px
    +media-breakpoint-down(md)
      max-width: 600px
    +media-breakpoint-down(sm)
      font-size: 20px
      line-height: 24px
      max-width: 400px
  video
    width: 100%
    max-width: calc(100% - 100px)
    margin: 50px 0 0 125px
    +media-breakpoint-down(lg)
      max-width: calc(100% - 30px)
      margin: 30px 0 0 70px
  img
    margin: 30px 0 0

// PRODUCTS CAROUSEL
.products-wrapper
  position: relative
  top: -10px
  padding-top: 190px
  background-color: white
  +media-breakpoint-down(md)
    top: 0
    padding-top: 100px
  +media-breakpoint-down(sm)
    padding-top: 70px
    .section-title:not(.large)
      font-size: 20px
      line-height: 24px
    .section-subtitle
      font-size: 15px
      line-height: 18px
  .product-options-row
    max-width: 950px
    > div
      padding: 0
    +media-breakpoint-down(md)
      max-width: 540px
    .product-options
      display: grid
      gap: 10px
      margin-bottom: 30px
      width: 100%
      grid-template-columns: repeat(8, 1fr)
      +media-breakpoint-down(md)
        grid-template-columns: repeat(4, 1fr)
      .product
        font-size: 12px
        border-radius: 3px
        display: flex
        flex-direction: column
        align-items: center
        text-align: center
        letter-spacing: normal
        line-height: 15px
        font-weight: normal
        padding: 20px 5px 17px
        cursor: pointer
        box-shadow: 0 0 7px 0 rgba(222,222,226,0.6)
        border: 1px solid rgba(222,222,226,0.36)
        position: relative
        overflow: hidden
        transition: border-color 0.1s ease-in, transform 0.3s ease-in-out
        &::after
          content: ""
          position: absolute
          bottom: -7px
          width: calc(100% - 4px)
          height: 7px
          border-radius: 4.5px 4.5px 0 0
          transition: bottom 0.2s ease-in
        &.active
          animation: activate 0.3s ease-in-out
          border-color: rgba(127,83,235,0.36)
          transform: scale(1)
          &::after
            background-color: #7f53eb
            bottom: 0
          &.fundamentals
            border-color: rgba(#42337E, 0.4)
            &::after
              background-color: #42337E
          &.drive
            border-color: rgba(#7D53EA, 0.4)
            &::after
              background-color: #7D53EA
          &.training
            border-color: rgba(#5fdeff, 0.4)
            &::after
              background-color: #5fdeff
          &.surveys
            border-color: rgba(#6ddcbe, 0.4)
            &::after
              background-color: #6ddcbe
          &.job-description-generator
            border-color: rgba(#ef73d0, 0.4)
            &::after
              background-color: #ef73d0
          &.policy-manual-wizard
            border-color: rgba(#ffca51, 0.4)
            &::after
              background-color: #ffca51
          &.live-hr-advice
            border-color: rgba(#4f63ff, 0.4)
            &::after
              background-color: #4f63ff
          &.hello-hr
            border-color: rgba(#FFE451, 0.4)
            &::after
              background-color: #FFE451
        &:focus-visible
          border-color: rgba(127,83,235,0.36)
        img
          width: 50px
          margin-bottom: 15px
  .product-details-container
    display: flex
    max-width: 950px
    height: 250px
    margin: 0 auto 50px
    +media-breakpoint-down(md)
      max-width: 540px
      height: 165px
    > div
      padding: 0
      &:first-of-type
        padding-right: 5px
        +media-breakpoint-down(md)
          padding: 15px
      &:last-of-type
        padding-left: 5px
    .product-details
      border: 1px solid rgba(127,83,235,0.36)
      border-radius: 4px
      width: 100%
      margin-right: -40px
      display: flex
      flex: 0 0 580px
      height: 240px
      +media-breakpoint-down(lg)
        flex: 0 0 560px
      +media-breakpoint-down(md)
        margin-right: 0
        flex-direction: column
        justify-content: space-between
        align-items: flex-start
        height: unset
        flex: unset
      .name-container
        flex: 0 0 240px
        height: calc(100% - 50px)
        margin: auto 0
        border-right: 1px solid rgba(127,83,235,0.36)
        display: flex
        flex-direction: column
        align-items: center
        +media-breakpoint-down(lg)
          flex: 0 0 220px
        +media-breakpoint-down(md)
          flex-direction: row
          gap: 15px
          border: none
          height: auto
          flex: 0 0 38px
          margin: 0
        img
          width: 118px
          height: 116px
          margin-bottom: 25px
          +media-breakpoint-down(md)
            width: 40px
            height: 38px
            margin: 0
        .name
          font-size: 20px
          font-weight: 900
          line-height: 19px
          text-align: center
          margin-bottom: 5px
          text-transform: capitalize
          +media-breakpoint-down(md)
            font-size: 17px
            font-weight: 700
          &.tall
            margin-top: -14px
            margin-bottom: 0
            +media-breakpoint-down(md)
              margin: auto 0
        a.btn
          padding: 0
          font-size: 20px
          +media-breakpoint-down(md)
            display: none
          img
            height: 18px
            width: 18px
            margin: 0 0 2px 5px
      .description
        font-size: 12px
        line-height: 15px
        margin: 0
      button
        padding: 0
        line-height: 1
      .word-carousel
        padding: 15px 5px 10px
        text-align: center
        width: 100%
        .title
          color: #006FD6
          font-size: 20px
          font-weight: 900
          line-height: 25px
          margin-bottom: 10px
  &.french
    .text-container
      grid-template-columns: minmax(0, 100%)
      +media-breakpoint-down(md)
        max-width: 600px
        margin: auto
      +media-breakpoint-down(sm)
        max-width: 400px
    .product-options
      grid-template-columns: repeat(6, 1fr)
      +media-breakpoint-down(md)
        grid-template-columns: repeat(3, 1fr)
    .product-details-container
      display: flex
      max-width: 950px
      height: 250px
      margin: 0 auto 50px
      +media-breakpoint-down(md)
        height: 185px
      .product-details
        .name-container
          flex: 0 0 210px
          +media-breakpoint-down(md)
            flex: 0 0 45px
            .name
              text-align: left
        .word-carousel__item
          font-size: 22px

.word-carousel-wrapper
  height: 100%
  width: 100%
  display: flex
  justify-content: center

.word-carousel
  position: relative
  width: 100%
  display: flex
  justify-content: center
  flex-direction: column

.word-carousel__item
  display: flex
  align-items: center
  position: absolute
  width: 100%
  opacity: 0
  font-size: 36px
  font-weight: 900
  text-align: center
  will-change: transform, opacity
  animation: word-carousel $animation-timing linear infinite
  > div
    width: 100%
    white-space: nowrap
    padding: 5px 0
    line-height: 1

.french
  .word-carousel__item
    animation: word-carousel-french $animation-timing linear infinite

@for $i from 1 through ($carousel-items - 1)
  .word-carousel__item:nth-child(#{$i})
    animation-delay: calc(#{$animation-delay-fraction} * #{$i - 4})

.word-carousel__item:last-child
  animation-delay: calc(-#{$animation-delay-fraction} * 4)

.fade-enter-active,
.fade-leave-active
    transition: opacity 0.4s ease

.fade-leave,
.fade-enter-to
    position: relative
    opacity: 1

.fade-enter,
.fade-leave-to
    position: absolute
    opacity: 0

.computer
  width: 100%
  height: 100%
  background-image: url("~@/assets/home/products/computer-md.png")
  background-size: 506px 285px
  background-repeat: no-repeat
  margin-bottom: 100px
  margin-right: -100px
  position: relative
  +media-breakpoint-down(md)
    background-size: 281px 159px
    margin-right: -75px
  .screens
    pointer-events: none
    position: absolute
    width: 326px
    left: 84px
    top: 30px
    border-bottom-left-radius: 24px
    border-bottom-right-radius: 24px
    overflow: hidden
    +media-breakpoint-down(md)
      width: 180px
      left: 47px
      top: 16px
      border-bottom-left-radius: 12px
      border-bottom-right-radius: 12px

// FOX BEHIND AWARDS
.awards-container
  margin: 150px 0 200px
  +media-breakpoint-down(md)
    margin: 100px 0
  +media-breakpoint-down(xs)
    margin: 50px 0

// CONTENT/IMAGE SECTIONS
.content-with-image
  +media-breakpoint-down(md)
    margin-bottom: 150px !important
  +media-breakpoint-down(xs)
    margin-bottom: 100px !important

.section-title
  font-size: 36px
  line-height: 44px
  margin: 0
  &:not(.large)
    +media-breakpoint-down(md)
      font-size: 30px
      line-height: 40px

.section-subtitle
  font-size: 24px
  line-height: 41px

.section-description
  font-size: 20px
  line-height: 30px
  margin: 30px 0
  +media-breakpoint-only(md)
    font-size: 16px
    line-height: 26px
    margin: 20px 0

.section-awards
  display: flex
  align-items: flex-start
  margin-bottom: 45px
  img
    + img
      margin-left: 25px
    &:first-of-type
      width: 120px
      margin-top: -6px
      +media-breakpoint-only(md)
        width: 100px
        margin-top: -5px
    &:nth-of-type(2),
    &:nth-of-type(3)
      width: 85px
      +media-breakpoint-only(md)
        width: 65px

.section-cta
  font-size: 20px
  padding: 9px 21px
  +media-breakpoint-only(md)
    font-size: 16px

// TRUSTED BY BLOB CAROUSEL
.trusted-by
  height: 375px
  background-image: url("~@/assets/home/Trusted By.png")
  background-position: center
  background-repeat: no-repeat
  background-size: 900px
  position: relative
  margin-bottom: 200px
  +media-breakpoint-down(md)
    height: 250px
    background-size: 550px
    margin-bottom: 100px
  +media-breakpoint-down(sm)
    height: 220px
    background-size: 450px
    margin-bottom: 60px
  +media-breakpoint-down(xs)
    background-size: calc(100vw - 60px)
  +media-breakpoint-down(xxs)
    background-size: calc(100vw - 20px)

.trusted-title
  color: white
  font-size: 36px
  position: absolute
  top: 20%
  left: 50%
  transform: translateX(-50%)
  width: 100%
  text-align: center
  +media-breakpoint-down(md)
    font-size: 25px
    top: 30%
  +media-breakpoint-down(sm)
    font-size: 21px
    top: 25%
  +media-breakpoint-down(xs)
    font-size: 18px
    top: 27%

.french
  .trusted-title
    font-size: 34px
    +media-breakpoint-down(md)
      font-size: 23px
    +media-breakpoint-down(sm)
      font-size: 19px
    +media-breakpoint-down(xs)
      font-size: 16px
      max-width: 95%

.slider-wrapper
  margin: 0 100px
  border: 1px solid rgba(222,222,226,0.36)
  border-radius: 4px
  box-shadow: 0 0 7px 0 rgba(222,222,226,0.6)
  background: white
  position: relative
  top: 50%
  +media-breakpoint-down(lg)
    margin: 0
  +media-breakpoint-down(md)
    margin: 0 20px
  +media-breakpoint-down(xs)
    margin: 0

// TESTIMONIALS
.testimonials
  .testimonial
    text-align: center
    margin-bottom: 50px
    img
      width: 220px
      +media-breakpoint-down(md)
        width: auto
    .quote
      max-width: 380px
      font-size: 18px
      line-height: 24px
      margin: 45px auto 30px
      +media-breakpoint-down(md)
        font-size: 14px
        line-height: 20px
        margin: 25px auto 15px
    .cite
      text-transform: uppercase
      font-size: 15px
      line-height: 18px
      font-style: italic

.footer
  color: white
  padding: 150px 0 100px
  +media-breakpoint-down(lg)
    padding: 50px 0
  +media-breakpoint-down(md)
    padding: 100px 0 50px
  +media-breakpoint-down(xxs)
    padding: 75px 0 30px
  video
    max-width: 100%
    +media-breakpoint-down(md)
      max-height: 310px
      margin-top: -50px
    +media-breakpoint-down(sm)
      max-height: 250px
      margin-top: -40px
    +media-breakpoint-down(xxs)
      margin-top: 0
  .title
    font-size: 40px
    line-height: 48px
    margin: 0 0 20px
    max-width: 360px
    +media-breakpoint-down(lg)
      font-size: 36px
      line-height: 44px
    +media-breakpoint-down(md)
      font-size: 30px
      line-height: 35px
    +media-breakpoint-down(xs)
      font-size: 28px
      line-height: 29px
  p
    font-size: 24px
    line-height: 32px
    margin: 0 0 20px
    max-width: 360px
    +media-breakpoint-down(lg)
      font-size: 20px
      line-height: 30px
    +media-breakpoint-down(md)
      font-size: 14px
      line-height: 17px
    +media-breakpoint-down(xs)
      font-size: 12px
      line-height: 15px
  a.btn
    background: white
    border-color: #006FD6
    color: #006FD6
    font-size: 20px
    +media-breakpoint-down(md)
      font-size: 16px
    +media-breakpoint-down(xs)
      font-size: 14px

img
  max-width: 100%

.custom-arrow
  height: 20px
  &:before
    content: none
  &.slick-prev
      left: -30px
  &.slick-next
      right: -30px
  +media-breakpoint-down(xs)
    display: none

.great-companies
  &__image
    display: flex
    align-items: center
    justify-content: center
    padding: 30px 20px
    height: 110px
    width: 100%
    +media-breakpoint-down(md)
      height: 67px
      padding: 10px 20px
    +media-breakpoint-down(sm)
      height: 55px
    img
      max-height: 100%

@keyframes activate
  50%
    transform: scale(.97)
  100%
    transform: scale(1)

@keyframes word-carousel
  0%
    transform: translateY(150%) scale(0.3)
    opacity: 0
    visibility: hidden
  #{$slide-change-timing}%,
  #{$animation-steps-fraction}%
    transform: translateY(150%) scale(0.3)
    opacity: .2
    visibility: visible
  #{$animation-steps-fraction + $slide-change-timing}%,
  #{$animation-steps-fraction * 2}%
    transform: translateY(100%) scale(0.55)
    opacity: 0.4
    visibility: visible
  #{($animation-steps-fraction * 2) + $slide-change-timing}%,
  #{$animation-steps-fraction * 3}%
    transform: translateY(0) scale(1)
    opacity: 1
    visibility: visible
  #{($animation-steps-fraction * 3) + $slide-change-timing}%,
  #{$animation-steps-fraction * 4}%
    transform: translateY(-100%) scale(0.55)
    opacity: .4
    visibility: visible
  #{($animation-steps-fraction * 4) + $slide-change-timing}%,
  #{$animation-steps-fraction * 5}%
    transform: translateY(-150%) scale(0.3)
    opacity: .2
    visibility: visible
  #{($animation-steps-fraction * 5) + $slide-change-timing}%
    transform: translateY(-150%) scale(0.3)
    opacity: 0
    visibility: visible
  100%
    transform: translateY(-150%) scale(0.3)
    opacity: 0
    visibility: hidden

@keyframes word-carousel-french
  0%
    transform: translateY(200%) scale(0.5)
    opacity: 0
    visibility: hidden
  #{$slide-change-timing}%,
  #{$animation-steps-fraction}%
    transform: translateY(200%) scale(0.5)
    opacity: .2
    visibility: visible
  #{$animation-steps-fraction + $slide-change-timing}%,
  #{$animation-steps-fraction * 2}%
    transform: translateY(125%) scale(0.65)
    opacity: 0.4
    visibility: visible
  #{($animation-steps-fraction * 2) + $slide-change-timing}%,
  #{$animation-steps-fraction * 3}%
    transform: translateY(0) scale(1)
    opacity: 1
    visibility: visible
  #{($animation-steps-fraction * 3) + $slide-change-timing}%,
  #{$animation-steps-fraction * 4}%
    transform: translateY(-125%) scale(0.65)
    opacity: .4
    visibility: visible
  #{($animation-steps-fraction * 4) + $slide-change-timing}%,
  #{$animation-steps-fraction * 5}%
    transform: translateY(-200%) scale(0.5)
    opacity: .2
    visibility: visible
  #{($animation-steps-fraction * 5) + $slide-change-timing}%
    transform: translateY(-200%) scale(0.5)
    opacity: 0
    visibility: visible
  100%
    transform: translateY(-200%) scale(0.5)
    opacity: 0
    visibility: hidden
</style>



// WEBPACK FOOTER //
// src/components/main/Home.vue